"use client";

import { useQuery } from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { createRelayClient, RelayApiError } from "../lib/client";
import type { RelayClient } from "../lib/client";
import type { RecoveryInfo } from "../lib/types";
import { isAddress } from "../lib/units";
import type { RecoverySuccessResult, RelayWalletProps } from "../types";
import { useRecoveryActions } from "./useRecovery";
import type { RecoveryAction, RecoveryState } from "./useRecovery";

let nextClientId = 1;
const clientIds = new WeakMap<object, number>();

function getClientId(client: RelayClient): number {
  const existing = clientIds.get(client);
  if (existing !== undefined) return existing;
  const id = nextClientId++;
  clientIds.set(client, id);
  return id;
}

export type RelayRecoveryQueryKey = readonly [
  "relay",
  "recovery",
  number,
  string | null,
  string | null,
];

/**
 * React Query policies accepted by useRelayRecovery. The SDK owns the request
 * identity and response shape, so callers cannot replace queryKey/queryFn or
 * inject/select recovery balances.
 */
export type RelayRecoveryQueryOptions = Omit<
  UseQueryOptions<RecoveryInfo, Error, RecoveryInfo, RelayRecoveryQueryKey>,
  | "queryKey"
  | "queryFn"
  | "select"
  | "initialData"
  | "initialDataUpdatedAt"
  | "placeholderData"
>;

export interface UseRelayRecoveryOptions {
  /** Pre-built client. Takes precedence over apiBaseUrl. */
  client?: RelayClient;
  /** Used to build a client via createRelayClient when client isn't passed. */
  apiBaseUrl?: string;
  /** Connected EVM wallet whose recoverable forwarder balances are queried. */
  recipient?: string;
  /** Optional CROSS delivery target passed to GET /v1/recovery. */
  target?: string;
  /** React Query lifecycle, cache, retry, and refetch policies. */
  query?: RelayRecoveryQueryOptions;
  /** Optional wallet capabilities. Read-only consumers may omit this. */
  wallet?: RelayWalletProps;
  /** Called when GET /v1/recovery returns 401. */
  onUnauthorized?: () => void;
  /** Called after a terminal query or recovery-action error. */
  onError?: (error: Error) => void;
  /** Called once after a recovery transaction confirms successfully. */
  onRecoverySuccess?: (result: RecoverySuccessResult) => void;
  /** Trusted factory override for a self-hosted deployment. */
  trustedFactories?: readonly string[];
}

export interface UseRelayRecoveryResult {
  info?: RecoveryInfo;
  loading: boolean;
  error?: Error;
  /** True for background refetches as well as the first request. */
  isFetching: boolean;
  /** Invalidates no identities; immediately refetches this hook's fixed query. */
  refresh: () => void;
  /** Signs a recovery transaction for the currently fetched info. */
  recover: (action: RecoveryAction, tokenAddress?: string) => Promise<void>;
  recoveryState: RecoveryState;
}

/**
 * Public Recovery API hook. Unlike the widget-internal useRecovery fetch, this
 * hook uses the host QueryClient and accepts React Query policies through
 * `query`. queryKey/queryFn and balance-shaping options remain SDK-owned.
 */
export function useRelayRecovery(
  opts: UseRelayRecoveryOptions = {},
): UseRelayRecoveryResult {
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const client = useMemo(() => {
    if (opts.client) return opts.client;
    if (!opts.apiBaseUrl) {
      throw new Error(
        "useRelayRecovery: either `client` or `apiBaseUrl` must be provided",
      );
    }
    return createRelayClient({ baseUrl: opts.apiBaseUrl });
  }, [opts.client, opts.apiBaseUrl]);

  const recipient = opts.recipient;
  const recipientKey = recipient && isAddress(recipient) ? recipient : undefined;
  const target = opts.target;
  const queryOptions = opts.query;
  const clientId = useMemo(() => getClientId(client), [client]);
  const queryKey = useMemo<RelayRecoveryQueryKey>(
    () => ["relay", "recovery", clientId, recipientKey ?? null, target ?? null],
    [clientId, recipientKey, target],
  );

  const recoveryQuery = useQuery({
    retry: false,
    ...queryOptions,
    queryKey,
    queryFn: async () => {
      if (!recipientKey) {
        throw new Error("useRelayRecovery: a valid EVM recipient is required");
      }
      return client.getRecovery({ user: recipientKey, target });
    },
    // An invalid/missing address always wins over a caller-supplied policy.
    enabled: recipientKey ? queryOptions?.enabled : false,
  });

  const handledErrorAtRef = useRef(0);
  useEffect(() => {
    const error = recoveryQuery.error;
    const errorUpdatedAt = recoveryQuery.errorUpdatedAt;
    if (!error || errorUpdatedAt === handledErrorAtRef.current) return;
    handledErrorAtRef.current = errorUpdatedAt;
    optsRef.current.onError?.(error);
    if (error instanceof RelayApiError && error.status === 401) {
      optsRef.current.onUnauthorized?.();
    }
  }, [recoveryQuery.error, recoveryQuery.errorUpdatedAt]);

  const refresh = useCallback(() => {
    void recoveryQuery.refetch();
  }, [recoveryQuery.refetch]);

  const actions = useRecoveryActions({
    recipient: opts.recipient,
    wallet: opts.wallet,
    trustedFactories: opts.trustedFactories,
    onError: opts.onError,
    onRecoverySuccess: opts.onRecoverySuccess,
    onChanged: refresh,
  });

  const info = recoveryQuery.data;
  const recover = useCallback(
    async (action: RecoveryAction, tokenAddress?: string) => {
      if (!info) return;
      await actions.recover(info, action, tokenAddress);
    },
    [actions, info],
  );

  return {
    info,
    loading: recoveryQuery.isLoading,
    error: recoveryQuery.error ?? undefined,
    isFetching: recoveryQuery.isFetching,
    refresh,
    recover,
    recoveryState: actions.stateFor(info?.forwarder),
  };
}
